import { supabase } from '../supabaseClient';
import type {
  ProgramFixedContentRowUpsert,
  ProgramProgressCellUpsert,
} from '../types/domain';
import type { ProgramContentSlideOwnerRef } from '../types/programContentSlideCanvas';
import {
  cloneLearningContentWorksheetDocument,
  createDefaultLearningContentWorksheetDocument,
  parseLearningContentWorksheetDocument,
  worksheetDocumentMeaningfulPageCount,
  type LearningContentWorksheetDocumentV1,
} from '../types/learningContentWorksheetCanvas';
import {
  parseProgramLearningContentFieldsFromEditor,
  serializeProgramLearningContentFieldsForEditor,
} from '../utils/programLearningContentFields';
import {
  learningContentFieldsToRpcParams,
  mergeLearningContentPatchWithCurrentDbBaseline,
  mergeLearningTextPatchWithCurrentDbBaseline,
} from '../utils/programLearningTextPatchMerge';
import { isPersistableUuid } from '../utils/programContentSlideOwner';
import { resolveWorksheetRefIdFromLearningText } from '../utils/programContentWorksheetOwner';
import { textForStorage } from '../utils/programContentNullNormalize';
import {
  invalidateProgramWorksheetListStatsCacheKeys,
  readProgramWorksheetListStatsCache,
  writeProgramWorksheetListStatsCache,
} from '../utils/programWorksheetListStatsCache';
import type { ProgramContentSlidePayloadV1 } from '../utils/programContentSlideStorage';
import { upsertProgramFixedContentRows } from './programFixedContentService';
import { upsertProgramProgressCells } from './programProgressService';
import { ensureScopedRowWithLearningAtPosition } from '../utils/scopedRecordSave';

export type ProgramWorksheetLearningTextPatch = {
  kind: 'program_cell' | 'fixed_row' | 'scoped_row';
  id: string;
  learningText: string;
};

function newWorksheetRefId(): string {
  const id = globalThis.crypto?.randomUUID?.();
  if (id) return id;
  return '00000000-0000-4000-8000-000000000000'.replace(/[08]/g, (c) => {
    const n = Number(c);
    const r = Math.floor(Math.random() * 16);
    return (n ^ (r & (15 >> (n / 4)))).toString(16);
  });
}

export function createWorksheetRefOnSave(): string {
  return newWorksheetRefId();
}

export function learningTextWithWorksheetRef(text: string | null | undefined, refId: string): string {
  const rid = refId.trim();
  if (!rid || !isPersistableUuid(rid)) return textForStorage(text) ?? '';
  const fields = parseProgramLearningContentFieldsFromEditor(text);
  return serializeProgramLearningContentFieldsForEditor({ ...fields, worksheetRefId: rid });
}

export function learningTextWithoutWorksheetRef(raw: string | null | undefined): string {
  const fields = parseProgramLearningContentFieldsFromEditor(raw);
  if (!fields.worksheetRefId) return textForStorage(raw) ?? '';
  return serializeProgramLearningContentFieldsForEditor({ ...fields, worksheetRefId: null });
}

export function forkWorksheetRefInLearningText(raw: string | null | undefined): {
  nextLearningText: string;
  fromRef: string;
  toRef: string;
} | null {
  const fromRef = resolveWorksheetRefIdFromLearningText(raw ?? '');
  if (!fromRef) return null;
  const toRef = newWorksheetRefId();
  return {
    nextLearningText: learningTextWithWorksheetRef(raw ?? '', toRef),
    fromRef,
    toRef,
  };
}

export async function fetchProgramContentWorksheet(
  worksheetRefId: string
): Promise<LearningContentWorksheetDocumentV1 | null> {
  const refId = worksheetRefId.trim();
  if (!refId || !isPersistableUuid(refId)) return null;
  const { data, error } = await supabase
    .from('program_content_worksheets')
    .select('document_json')
    .eq('worksheet_ref_id', refId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data?.document_json) return null;
  return parseLearningContentWorksheetDocument(data.document_json) ?? createDefaultLearningContentWorksheetDocument();
}

export async function upsertProgramContentWorksheet(params: {
  workspaceId: string;
  worksheetRefId: string;
  document: LearningContentWorksheetDocumentV1;
  learningPatch?: ProgramWorksheetLearningTextPatch | null;
}): Promise<string> {
  const sid = params.workspaceId.trim();
  const refId = params.worksheetRefId.trim();
  if (!sid) throw new Error('workspaceId required');
  if (!refId || !isPersistableUuid(refId)) throw new Error('worksheetRefId required');

  const documentJson = cloneLearningContentWorksheetDocument(params.document);
  let patch = params.learningPatch ?? null;
  if (patch?.id?.trim() && patch.learningText) {
    try {
      const mergedLearningText = await mergeLearningTextPatchWithCurrentDbBaseline({
        workspaceId: sid,
        kind: patch.kind,
        id: patch.id.trim(),
        learningText: patch.learningText,
      });
      patch = { ...patch, learningText: mergedLearningText };
    } catch {
      /* baseline 조회 실패 시 기존 patch 그대로 시도 */
    }
  }

  const learningRpc = patch
    ? learningContentFieldsToRpcParams(
        await mergeLearningContentPatchWithCurrentDbBaseline({
          workspaceId: sid,
          kind: patch.kind,
          id: patch.id.trim(),
          learningText: patch.learningText,
        }).catch(() => parseProgramLearningContentFieldsFromEditor(patch!.learningText))
      )
    : null;

  const { data, error } = await supabase.rpc('upsert_program_content_worksheet', {
    p_workspace_id: sid,
    p_worksheet_ref_id: refId,
    p_document_json: documentJson,
    p_learning_kind: patch?.kind ?? null,
    p_learning_id: patch?.id?.trim() ?? null,
    p_learning_text: learningRpc?.p_learning_text ?? patch?.learningText ?? null,
    p_learning_note: learningRpc?.p_learning_note ?? null,
    p_presentation_ref_id: learningRpc?.p_presentation_ref_id ?? null,
    p_learning_worksheet_ref_id: learningRpc?.p_worksheet_ref_id ?? null,
  });
  if (error) throw new Error(error.message);
  invalidateProgramWorksheetListStatsCacheKeys(sid, [refId]);
  return String(data ?? refId);
}

export async function deleteProgramContentWorksheet(refId: string, workspaceId: string): Promise<void> {
  const rid = refId.trim();
  const sid = workspaceId.trim();
  if (!rid || !sid) return;
  const { error } = await supabase
    .from('program_content_worksheets')
    .delete()
    .eq('worksheet_ref_id', rid)
    .eq('workspace_id', sid);
  if (error) throw new Error(error.message);
  invalidateProgramWorksheetListStatsCacheKeys(sid, [rid]);
}

export async function copyWorksheetBetweenOwners(
  workspaceId: string,
  fromRefId: string,
  toRefId: string
): Promise<void> {
  const sid = workspaceId.trim();
  const fromId = fromRefId.trim();
  const toId = toRefId.trim();
  if (!sid || !isPersistableUuid(fromId) || !isPersistableUuid(toId) || fromId === toId) return;
  const doc = await fetchProgramContentWorksheet(fromId);
  if (!doc) return;
  await upsertProgramContentWorksheet({
    workspaceId: sid,
    worksheetRefId: toId,
    document: doc,
  });
}

export async function forkWorksheetRefAfterScopedOrFixedRowDuplicate(params: {
  workspaceId: string;
  table: 'program_scoped_content_rows' | 'program_fixed_content_rows';
  newRowId: string;
  learningText: string | null | undefined;
}): Promise<void> {
  const sid = params.workspaceId.trim();
  const newId = params.newRowId.trim();
  if (!sid || !newId) return;
  const forked = forkWorksheetRefInLearningText(params.learningText);
  if (!forked) return;
  try {
    await copyWorksheetBetweenOwners(sid, forked.fromRef, forked.toRef);
  } catch {
    try {
      const stripped = learningTextWithoutWorksheetRef(params.learningText);
      await supabase.from(params.table).update({ learning_text: stripped }).eq('id', newId);
    } catch {
      /* */
    }
    return;
  }
  try {
    const { error } = await supabase
      .from(params.table)
      .update({ learning_text: forked.nextLearningText })
      .eq('id', newId);
    if (error) throw new Error(error.message);
  } catch {
    /* learning_text 갱신 실패는 본 저장과 분리 */
  }
}

export type WorksheetRefStats = {
  pageCount: number;
  documentBytes: number;
};

function worksheetDocumentJsonByteSize(raw: unknown): number {
  try {
    return new Blob([JSON.stringify(raw ?? {})]).size;
  } catch {
    return String(raw ?? '').length;
  }
}

/** ref별 저장된 활동지 페이지 수(0|1)·document_json 용량(추정) */
/**
 * owner(활동지 ref)별 페이지 수·문서 용량(추정). LS 캐시로 재조회를 최소화한다.
 * `forceRefresh` 없으면 캐시에 없는 refId만 DB의 무거운 `document_json`을 조회한다.
 */
export async function fetchWorksheetRefStatsMap(
  workspaceId: string,
  refIds: string[],
  options?: { forceRefresh?: boolean }
): Promise<Map<string, WorksheetRefStats>> {
  const sid = workspaceId.trim();
  const ids = Array.from(new Set(refIds.map((id) => id.trim()).filter((id) => isPersistableUuid(id))));
  const out = new Map<string, WorksheetRefStats>();
  if (!sid || !ids.length) return out;

  if (!options?.forceRefresh) {
    const cached = readProgramWorksheetListStatsCache(sid, ids);
    cached.forEach((stats, key) => out.set(key, stats));
  }
  const missingIds = ids.filter((id) => !out.has(id));
  if (!missingIds.length) return out;

  const { data, error } = await supabase
    .from('program_content_worksheets')
    .select('worksheet_ref_id, document_json')
    .eq('workspace_id', sid)
    .in('worksheet_ref_id', missingIds);
  if (error) throw new Error(error.message);
  const fetched = new Map<string, WorksheetRefStats>();
  for (const refId of missingIds) {
    fetched.set(refId, { pageCount: 0, documentBytes: 0 });
  }
  for (const row of data ?? []) {
    const refId = String((row as { worksheet_ref_id?: string }).worksheet_ref_id ?? '').trim();
    if (!refId) continue;
    const docJson = (row as { document_json?: unknown }).document_json;
    const doc = parseLearningContentWorksheetDocument(docJson);
    fetched.set(refId, {
      pageCount: doc ? worksheetDocumentMeaningfulPageCount(doc) : 0,
      documentBytes: worksheetDocumentJsonByteSize(docJson),
    });
  }
  writeProgramWorksheetListStatsCache(sid, fetched);
  fetched.forEach((stats, key) => out.set(key, stats));
  return out;
}

export async function fetchProgramContentWorksheetPresence(
  workspaceId: string,
  refIds: string[]
): Promise<Set<string>> {
  const sid = workspaceId.trim();
  const ids = Array.from(new Set(refIds.map((id) => id.trim()).filter((id) => isPersistableUuid(id))));
  if (!sid || !ids.length) return new Set();
  const { data, error } = await supabase
    .from('program_content_worksheets')
    .select('worksheet_ref_id')
    .eq('workspace_id', sid)
    .in('worksheet_ref_id', ids);
  if (error) throw new Error(error.message);
  return new Set((data ?? []).map((row) => String((row as { worksheet_ref_id?: string }).worksheet_ref_id ?? '').trim()).filter(Boolean));
}

function splitWorkspaceScope(scopeId: string): { scopeType: 'fixed' | 'track' | 'project' | ''; scopeKey: string } {
  const parts = scopeId.split('|');
  const scopeType = parts[0] === 'fixed' || parts[0] === 'track' || parts[0] === 'project' ? parts[0] : '';
  return { scopeType, scopeKey: parts.slice(5).join('|') };
}

function toWorksheetLearningPatch(
  owner: ProgramContentSlideOwnerRef,
  learningText: string
): ProgramWorksheetLearningTextPatch | null {
  const id = owner.id.trim();
  if (!id || !isPersistableUuid(id)) return null;
  if (owner.kind === 'program_cell' || owner.kind === 'fixed_row' || owner.kind === 'scoped_row') {
    return { kind: owner.kind, id, learningText };
  }
  return null;
}

async function createProgramRowOwnerForWorksheetSave(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  scopeId: string;
  learningText: string;
  ctx: NonNullable<ProgramContentSlidePayloadV1['createPresentationRefOnSave']>;
}): Promise<ProgramContentSlideOwnerRef | null> {
  const sid = params.workspaceId.trim();
  const gradeId = (params.ctx.gradeId ?? '').trim();
  if (!sid || !gradeId) return null;
  const { scopeType, scopeKey } = splitWorkspaceScope(params.scopeId);
  const common = {
    workspace_id: sid,
    grade_id: gradeId,
    academic_year: params.academicYear,
    term_number: params.termNumber,
    timetable_text: params.ctx.timetableText ?? '',
    unit_text: params.ctx.unitText ?? '',
    learning_text: params.learningText,
    page_text: params.ctx.pageText ?? '',
    assistant_page_text: params.ctx.assistantPageText ?? '',
    current_period_text: params.ctx.currentPeriodText ?? '',
    total_period_text: params.ctx.totalPeriodText ?? '',
    material_text: params.ctx.materialText ?? '',
    record_cross_curricular_text: params.ctx.recordCrossCurricularText ?? '',
    record_memo_text: params.ctx.recordMemoText ?? '',
    record_evaluation_text: params.ctx.recordEvaluationText ?? '',
    record_class_text: params.ctx.recordClassText ?? '',
    bg_color_by_field: {},
  };

  if (params.ctx.saveMode === 'fixed_content' && scopeType === 'fixed') {
    const row: ProgramFixedContentRowUpsert = {
      ...common,
      order_index: Number.isFinite(params.ctx.orderIndex) ? Number(params.ctx.orderIndex) : 0,
      source_annual_cell_key: params.ctx.sourceAnnualCellKey ?? params.ctx.annualKey,
      event_name: null,
    };
    const saved = await upsertProgramFixedContentRows([row]);
    const id = saved[0]?.id?.trim();
    return id ? { kind: 'fixed_row', id } : null;
  }

  if (params.ctx.saveMode === 'fixed_content' && (scopeType === 'track' || scopeType === 'project') && scopeKey.trim()) {
    /** 일정 위치(몇 번째 수업)의 행을 찾고, 없을 때만 그 자리에 하나 만든다(중복 행 방지) */
    const savedRow = await ensureScopedRowWithLearningAtPosition(
      {
        workspaceId: common.workspace_id,
        academicYear: common.academic_year,
        termNumber: common.term_number,
        gradeId: common.grade_id,
        scopeType,
        scopeKey,
      },
      Number.isFinite(params.ctx.orderIndex) ? Number(params.ctx.orderIndex) : 0,
      common
    );
    const id = savedRow.id?.trim();
    return id ? { kind: 'scoped_row', id } : null;
  }

  const row: ProgramProgressCellUpsert = {
    ...common,
    monday_iso: params.ctx.mondayIso ?? '',
    weekday_key: (params.ctx.weekdayKey ?? 'mon') as ProgramProgressCellUpsert['weekday_key'],
    slot_index: Number.isFinite(params.ctx.slotIndex) ? Number(params.ctx.slotIndex) : 0,
    record_member_group_id: null,
  };
  const saved = await upsertProgramProgressCells([row]);
  const id = saved[0]?.id?.trim();
  return id ? { kind: 'program_cell', id } : null;
}

/** 연간진도표에서 활동지 저장 시 프로그램 원본 행 learning_text 패치(없으면 생성) */
export async function resolveOrCreateAnnualWorksheetLearningPatch(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  nextLearningText: string;
  slideOpenDbOwner: ProgramContentSlideOwnerRef | null;
  slideWorkspaceScopeId: string;
  createProgramRowContext?: ProgramContentSlidePayloadV1['createPresentationRefOnSave'];
}): Promise<ProgramWorksheetLearningTextPatch | null> {
  const existing = params.slideOpenDbOwner
    ? toWorksheetLearningPatch(params.slideOpenDbOwner, params.nextLearningText)
    : null;
  if (existing) return existing;
  if (!params.createProgramRowContext) return null;
  const owner = await createProgramRowOwnerForWorksheetSave({
    workspaceId: params.workspaceId,
    academicYear: params.academicYear,
    termNumber: params.termNumber,
    scopeId: params.slideWorkspaceScopeId,
    learningText: params.nextLearningText,
    ctx: params.createProgramRowContext,
  });
  return owner ? toWorksheetLearningPatch(owner, params.nextLearningText) : null;
}
